import React from 'react';
import { Link } from 'react-router-dom';

/**
 * EmptyState - Standardized empty state component
 * @param {string} icon - Bootstrap icon class (e.g., 'bi-inbox')
 * @param {string} title - Main title text
 * @param {string} message - Description message
 * @param {string} actionLabel - Button label
 * @param {string} actionLink - Link for button (optional)
 * @param {Function} actionHandler - Click handler for button (optional)
 * @param {string} variant - Variant: 'default', 'cart', 'wishlist', 'orders', 'products', 'admin'
 * @param {string} className - Additional CSS classes
 */
const EmptyState = ({
  icon = 'bi-inbox',
  title,
  message,
  actionLabel,
  actionLink,
  actionHandler,
  variant = 'default',
  className = '',
  children,
}) => {
  const iconVariants = {
    cart: 'bi-bag',
    wishlist: 'bi-heart',
    orders: 'bi-receipt',
    products: 'bi-box-seam',
    admin: 'bi-folder-x',
    default: icon,
  };

  const defaultMessages = {
    cart: "Your cart is waiting for you! Add products you love and they'll appear here.",
    wishlist: "Start building your wishlist! Save items you love for later.",
    orders: "No orders yet. Start shopping to see your order history here.",
    products: "No products found. Try adjusting your search or filters.",
    admin: "No items found. Create your first item to get started.",
    default: message || "Nothing here yet. Get started by adding your first item.",
  };

  const defaultTitles = {
    cart: 'Your Cart is Empty',
    wishlist: 'Your Wishlist is Empty',
    orders: 'No Orders Yet',
    products: 'No Products Found',
    admin: 'No Items Found',
    default: title || 'Nothing Here',
  };

  const defaultActionLabels = {
    cart: 'Browse Products',
    wishlist: 'Browse Products',
    orders: 'Browse Products',
    products: 'Clear Filters',
    admin: 'Create New',
    default: actionLabel || 'Get Started',
  };

  const finalIcon = iconVariants[variant] || icon;
  const finalTitle = title || defaultTitles[variant] || defaultTitles.default;
  const finalMessage = message || defaultMessages[variant] || defaultMessages.default;
  const finalActionLabel = actionLabel || defaultActionLabels[variant] || defaultActionLabels.default;

  const renderActionButton = () => {
    if (!actionLabel && !finalActionLabel) return null;

    if (actionLink) {
      return (
        <Link to={actionLink} className="btn btn-primary empty-state-action">
          <i className={`bi bi-arrow-right me-2`}></i>
          {actionLabel || finalActionLabel}
        </Link>
      );
    }

    if (actionHandler) {
      return (
        <button
          className="btn btn-primary empty-state-action"
          onClick={actionHandler}
          type="button"
        >
          <i className={`bi bi-arrow-right me-2`}></i>
          {actionLabel || finalActionLabel}
        </button>
      );
    }

    return null;
  };

  return (
    <div className={`empty-state empty-state-${variant} ${className}`}>
      <div className="empty-state-content">
        <div className="empty-state-icon-wrapper">
          <i className={`bi ${finalIcon} empty-state-icon`}></i>
        </div>
        <h2 className="empty-state-title">{finalTitle}</h2>
        <p className="empty-state-message">{finalMessage}</p>
        {children && <div className="empty-state-children">{children}</div>}
        {renderActionButton()}
      </div>
    </div>
  );
};

export default EmptyState;
